import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
	ReactFlow,
	useReactFlow,
	Controls,
} from '@xyflow/react';
import { useSelector, useDispatch } from 'react-redux';
import { nodeTypes } from '~/components/CustomNodes';
import CustomEdge from '~/components/CustomEdge';
import CustomSmoothStepEdge from '~/components/CustomSmoothStepEdge';
import { useFlowFunctions } from '~/functions/flowFunctions';
import '@xyflow/react/dist/style.css';

// Reducers
import { setNodes, setEdges, setRects } from '~/reducers';
import { initialNodes } from '~/reducers/nodesReducer';
import { initialEdges } from '~/reducers/edgesReducer';

// Functions
import { organizeNodes, cancelExecution, restaurarAvatarsAoEstadoOriginal } from '~/functions/nodeFunctions';
import { validateAllNodesPendencies } from '~/utils/nodeValidation';

// Contexts
import { useUnsavedChangesContext } from '~/contexts/UnsavedChangesContext';

// Hooks
import { useUnsavedChanges } from '~/hooks/useUnsavedChanges';

// Styles
import '~/styles/ReactFlow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faArrowLeft, faArrowRight, faRedo } from '@fortawesome/free-solid-svg-icons';

import { myStore } from '~/App';
import { setExecuting } from '~/actions/nodes';
import ValidationModal from '~/components/ValidationModal';
import DevelopmentModal from '~/components/DevelopmentModal';
import PendenciesModal from '~/components/PendenciesModal';
import OperatorModal from '~/components/OperatorModal';
import { useModal } from '~/contexts/ModalContext';
import ConnectPreviewTooltip from '~/components/ConnectPreviewTooltip';

const connectionLineStyle = {
	strokeWidth: 3,
	stroke: '#7b3fc0',
};

const defaultEdgeOptions = {
	style: { strokeWidth: 3, stroke: '#7b3fc0' },
	markerEnd: { type: 'arrowclosed', color: '#7b3fc0' },
};

// Tipos de edge personalizados
const edgeTypes = {
	default: CustomEdge,
	smoothstep: CustomSmoothStepEdge,
};

export const FlowChart = ({ isCollapsed }) => {
	const reactFlowWrapper = useRef(null);
	const { screenToFlowPosition, nodeInternals, fitView } = useReactFlow();
	const [highlightedEdgeIds, setHighlightedEdgeIds] = useState([]);
	const nodes = useSelector((state) => state.nodes.nodes);
	const edges = useSelector((state) => state.edges.edges);
	const dispatch = useDispatch();

	// Context para mudanças não salvas
	const { hasUnsavedChanges, markAsChanged } = useUnsavedChangesContext();
	
	// Hook para gerenciar aviso ao sair da página
	useUnsavedChanges(
		hasUnsavedChanges,
		"Você tem alterações não salvas no diagrama. Tem certeza que deseja sair sem fazer o download das últimas alterações?"
	);

	// Handler para clicks nas edges (delete)
	const handleEdgeClick = useCallback((event, edgeData) => {
		console.log('🗑️ Deletando edge:', edgeData.id);
		if (edgeData.action === 'delete') {
			const updatedEdges = edges.filter(edge => edge.id !== edgeData.id);
			dispatch(setEdges(updatedEdges));
			markAsChanged();
		}
	}, [edges, dispatch, markAsChanged]);

	// Histórico de snapshots para desfazer/refazer
	const skipRecordRef = useRef(false);
	const [history, setHistory] = useState([]);
	const [historyIndex, setHistoryIndex] = useState(-1);
	const [fitViewExecuted, setFitViewExecuted] = useState(false);

	// Adiciona snapshot inicial ao histórico (store full nodes and edges)
	useEffect(() => {
		// deep clone initial snapshot
		const clonedNodes = JSON.parse(JSON.stringify(nodes));
		const clonedEdges = JSON.parse(JSON.stringify(edges));
		setHistory([{ nodes: clonedNodes, edges: clonedEdges }]);
		setHistoryIndex(0);
		prevNodesRef.current = nodes;
		prevEdgesRef.current = edges;
	}, []);

	// Histórico: registra snapshot apenas quando adiciona/remove node ou muda node.data, ignorando posição e edges
	const prevNodesRef = useRef(nodes);
	// Track previous edges to record history on count changes
	const prevEdgesRef = useRef(edges);

	useEffect(() => {
		const prevNodes = prevNodesRef.current;
		const prevEdges = prevEdgesRef.current;
		// record when edges or nodes count change
		let shouldRecord = edges.length !== prevEdges.length || nodes.length !== prevNodes.length;
		// if counts equal, record if any node data changed
		if (!shouldRecord) {
			for (let i = 0; i < nodes.length; i++) {
				if (JSON.stringify(prevNodes[i]?.data) !== JSON.stringify(nodes[i].data)) {
					shouldRecord = true;
					break;
				}
			}
		}
		if (shouldRecord) {
			if (skipRecordRef.current) {
				skipRecordRef.current = false;
			} else {
				// Marca como não salvo quando há mudanças
				markAsChanged();
				
				// deep clone snapshot to avoid mutating history
				const clonedNodes = JSON.parse(JSON.stringify(nodes));
				const clonedEdges = JSON.parse(JSON.stringify(edges));
				const snapshot = { nodes: clonedNodes, edges: clonedEdges };
				setHistory(prevHistory => {
					const truncated = prevHistory.slice(0, historyIndex + 1);
					return truncated.concat(snapshot);
				});
				setHistoryIndex(prevIndex => prevIndex + 1);
			}
		}
		prevNodesRef.current = nodes;
		prevEdgesRef.current = edges;
	}, [nodes, edges, markAsChanged]);

	// Handler 'Voltar' (undo)
	const handleBack = () => {
		if (historyIndex > 0) {
			skipRecordRef.current = true;
			const snapshot = history[historyIndex - 1];
			// restore full nodes array to undo creation/deletion
			myStore.dispatch(setNodes(snapshot.nodes));
			myStore.dispatch(setEdges(snapshot.edges));
			setHistoryIndex(historyIndex - 1);
			// Marca como alterado após desfazer
			markAsChanged();
		}
	};

	// Handler 'Avançar' (redo)
	const handleForward = () => {
		if (historyIndex < history.length - 1) {
			skipRecordRef.current = true;
			const snapshot = history[historyIndex + 1];
			// restore full nodes array to redo creation/deletion
			myStore.dispatch(setNodes(snapshot.nodes));
			myStore.dispatch(setEdges(snapshot.edges));
			setHistoryIndex(historyIndex + 1);
			// Marca como alterado após refazer
			markAsChanged();
		}
	};

	// Handler 'Exportar' via Ctrl+S
	const handleExportKeyboard = () => {
		console.log('🎯 Ctrl+S pressionado - disparando exportação');
		// Dispara evento customizado para que o Header capture e execute a exportação
		const event = new CustomEvent('triggerExport', {
			detail: { exportType: 'normal' }
		});
		window.dispatchEvent(event);
	};

	// Keyboard shortcuts: Ctrl+Z undo, Ctrl+Y redo, Ctrl+S export
	useEffect(() => {
		const keyHandler = (e) => {
			if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
				e.preventDefault();
				handleBack();
			}
			if (e.ctrlKey && e.key.toLowerCase() === 'y') {
				e.preventDefault();
				handleForward();
			}
			if (e.ctrlKey && e.key.toLowerCase() === 's') {
				e.preventDefault();
				handleExportKeyboard();
			}
		};
		document.addEventListener('keydown', keyHandler);
		return () => document.removeEventListener('keydown', keyHandler);
	}, [handleBack, handleForward]);

	const { onConnect, onDragOver, onDrop, onDragLeave, onNodesChange, onEdgesChange, onNodeDragStop, onNodeDrag, onNodeDragStart, onDelete } = useFlowFunctions(
		screenToFlowPosition,
		defaultEdgeOptions,
		highlightedEdgeIds,
		setHighlightedEdgeIds,
		nodeInternals,
		nodeTypes
	);

    const isExecuting = useSelector(state => state.nodes.isExecuting);
    const [tooltipPos, setTooltipPos] = useState(null);
	const [validationErrors, setValidationErrors] = useState([]);
    const [validationModal, setValidationModal] = useState({ show: false, pendencies: [] });
    const [operatorModal, setOperatorModal] = useState({ show: false, operatorType: '' });
    const [highlightedNodeId, setHighlightedNodeId] = useState(null);
    const { developmentModal, pendenciesModal, hideDevelopmentModal, hidePendenciesModal } = useModal();

    // Listener para modal de operador
    useEffect(() => {
        const handleShowOperatorModal = (event) => {
            setOperatorModal({ show: true, operatorType: event.detail.operatorType });
        };

        window.addEventListener('showOperatorModal', handleShowOperatorModal);
        return () => window.removeEventListener('showOperatorModal', handleShowOperatorModal);
    }, []);

    const hideOperatorModal = () => {
        setOperatorModal({ show: false, operatorType: '' });
    };

    // Limpa a tooltip quando a execução termina
    useEffect(() => {
        if (!isExecuting && tooltipPos) {
            setTooltipPos(null);
        }
    }, [isExecuting, tooltipPos]);

    const handleExecute = async () => {
        // Validação usando a nova função dinâmica
        const allPendencies = validateAllNodesPendencies(nodes);
        
        if (allPendencies.length > 0) {
            setValidationModal({ show: true, pendencies: allPendencies });
            return;
        }

        await executeFlow();
    };

    const executeFlow = async () => {
        myStore.dispatch(setExecuting(true));
        fitView({ padding: 0.1 });
        await organizeNodes();
        myStore.dispatch(setExecuting(false));
    };

    const handleExecuteAnyway = async () => {
        // Fecha o modal primeiro
        setValidationModal({ show: false, pendencies: [] });
        // Executa o fluxo mesmo com avisos
        await executeFlow();
    };

    const handleCorrectPendencies = () => {
        if (validationModal.pendencies.length > 0) {
            // Pega o primeiro nó com pendência
            const firstNodeWithPendency = validationModal.pendencies[0];
            
            // Encontra o nó no ReactFlow
            const targetNode = nodes.find(node => node.id === firstNodeWithPendency.nodeId);
            
            if (targetNode) {
                // Fecha o modal primeiro
                setValidationModal({ show: false, pendencies: [] });
                
                // Faz zoom no nó com animação progressiva
                setTimeout(() => {
                    fitView({
                        nodes: [targetNode],
                        padding: 0.3,
                        duration: 800, // Animação mais lenta e suave
                        minZoom: 1.2,
                        maxZoom: 2.0
                    });
                    
                    // Destaca o nó por alguns segundos
                    setHighlightedNodeId(targetNode.id);
                    setTimeout(() => {
                        setHighlightedNodeId(null);
                    }, 3000);
                }, 100);
            }
        } else {
            // Se não há pendências, apenas fecha o modal
            setValidationModal({ show: false, pendencies: [] });
        }
    };

    // Handler to stop execution
    const handleCancel = () => {
        cancelExecution();
        restaurarAvatarsAoEstadoOriginal();
        myStore.dispatch(setExecuting(false));
        myStore.dispatch(setRects([]));
    };

    // Handler to reset flow to initial state
    const handleReset = () => {
        // reset nodes and edges to initial configuration
        myStore.dispatch(setNodes(initialNodes.nodes));
        myStore.dispatch(setEdges(initialEdges.edges));
		setFitViewExecuted(false);
		// Marca como alterado após reset
		markAsChanged();
    };

	useEffect(() => {
		console.log(nodes)
	}, [nodes])

	useEffect(() => {
		if (nodes.length > 0 && !fitViewExecuted) {
			fitView({ padding: 0.1 });
			setFitViewExecuted(true);
		}
	}, [nodes, edges])

	return (
		<div
			className={`react-flow-wrapper ${isCollapsed ? 'sidebar-collapsed' : ''}`}
			style={{ height: '100%', width: "38%", position: "relative" }}
			ref={reactFlowWrapper}
			onDragLeave={onDragLeave}
		>
			<button
                className="execute-button"
                onClick={isExecuting ? handleCancel : handleExecute}
                aria-label={isExecuting ? "Parar" : "Executar"}
                title={isExecuting ? "Parar execução" : "Executar fluxo"}
            >
                <FontAwesomeIcon icon={isExecuting ? faStop : faPlay} />
            </button>
			{/* Botões Voltar (undo) e Avançar (redo) */}
			<button
				className="back-button"
				onClick={handleBack}
				disabled={historyIndex <= 0}
				aria-label="Voltar"
				title="Desfazer última alteração"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
			</button>
			<button
				className="forward-button"
				onClick={handleForward}
				disabled={historyIndex >= history.length - 1}
				aria-label="Avançar"
				title="Refazer alteração"
			>
				<FontAwesomeIcon icon={faArrowRight} />
			</button>
			<button
				className="reset-button"
				onClick={handleReset}
				title="Resetar fluxograma"
			>
				<FontAwesomeIcon icon={faRedo} />
			</button>
			<ReactFlow
				nodes={nodes.map(node => ({
					...node,
					data: {
						...node.data,
						highlighted: highlightedNodeId === node.id
					}
				}))}
				edges={edges.map(edge => {
					// Encontra o nó de origem para verificar o tipo
					const sourceNode = nodes.find(node => node.id === edge.source);
					
					// Se a edge vem de um RepetirNode E é do handle "loop", aplica cor laranja
					if (sourceNode && sourceNode.type === 'Repetir' && edge.sourceHandle === 'loop') {
						return {
							...edge,
							type: 'smoothstep', // Edge mais arredondada
							className: 'loop-edge-no-drop', // Classe específica para CSS
							style: { 
								strokeWidth: 3, 
								stroke: '#ff9800' // Cor laranja para o loop
							},
							markerEnd: { 
								type: 'arrowclosed', 
								color: '#ff9800' 
							},
							data: { 
								...edge.data, 
								onEdgeClick: handleEdgeClick,
								isLoopEdge: true 
							},
							interactionWidth: 0, // Desabilita interação com a edge para drop
							focusable: false, // Não focável
							selectable: false, // Não selecionável
							deletable: false, // Não deletável por acidente via teclado
							zIndex: -1 // Coloca atrás para não interferir no drop
						};
					}
					
					// Para outras edges (incluindo saída normal do Repetir), mantém a cor padrão roxa
					return {
						...edge,
						data: { 
							...edge.data, 
							onEdgeClick: handleEdgeClick 
						}
					};
				})}
				nodesDraggable={!isExecuting}
				nodesConnectable={!isExecuting}
				elementsSelectable={!isExecuting}
				panOnDrag={!isExecuting}
				panOnScroll={!isExecuting}
				zoomOnScroll={!isExecuting}
				zoomOnPinch={!isExecuting}
				zoomOnDoubleClick={!isExecuting}
				onDrop={onDrop}
				onDragOver={onDragOver}
				onDelete={onDelete}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onConnect={onConnect}
				onNodeDragStop={onNodeDragStop}
				onNodeDrag={onNodeDrag}
				onNodeDragStart={onNodeDragStart}
				minZoom={0.2}
				maxZoom={2}
				defaultEdgeOptions={defaultEdgeOptions}
				connectionLineStyle={connectionLineStyle}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				style={{ backgroundColor: '#e4e4e4' }}
			>
				<Controls />
			</ReactFlow>
			{/* Tooltip de pré-visualização de conexão */}
			<ConnectPreviewTooltip />
			{validationErrors.length > 0 && (
               <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                   <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
                       <h2>Pendências encontradas</h2>
                       <ul>
                           {validationErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                       </ul>
                       <button onClick={() => setValidationErrors([])} style={{ marginTop: '12px', padding: '8px 16px', cursor: 'pointer' }}>Fechar</button>
                   </div>
               </div>
           )}
			{isExecuting && (
				<div
					className="flow-overlay"
					onMouseMove={(e) => setTooltipPos({ x: e.clientX + 10, y: e.clientY + 10 })}
					onMouseLeave={() => setTooltipPos(null)}
				/>
			)}
			{tooltipPos && (
				<div
					className="flow-tooltip"
					style={{ left: tooltipPos.x, top: tooltipPos.y }}
				>
					Pare a execução para editar o fluxograma
				</div>
			)}
            
            {/* Modal de validação */}
            {validationModal.show && (
                <ValidationModal
                    pendencies={validationModal.pendencies}
                    onClose={handleCorrectPendencies}
                    onExecuteAnyway={handleExecuteAnyway}
                />
            )}
            
            {/* Modal de desenvolvimento */}
            {developmentModal.show && (
                <DevelopmentModal
                    nodeName={developmentModal.nodeName}
                    onClose={hideDevelopmentModal}
                />
            )}
            
            {/* Modal de pendências */}
            {pendenciesModal.show && (
                <PendenciesModal
                    pendencies={pendenciesModal.pendencies}
                    nodeName={pendenciesModal.nodeName}
                    onClose={hidePendenciesModal}
                />
            )}

            {/* Modal de operador */}
            {operatorModal.show && (
                <OperatorModal
                    operatorType={operatorModal.operatorType}
                    onClose={hideOperatorModal}
                />
            )}
		</div>
	);
};

